import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm, UseFormReturn, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2Icon, ChevronDownIcon, CopyIcon } from 'lucide-react';

import { queryClient } from 'api';
import { EditableSession } from 'types/event.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { Button } from '@epoch-ui/components/Button/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel
} from '@epoch-ui/components/AlertDialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent
} from '@epoch-ui/components/DropdownMenu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter
} from '@epoch-ui/components/Sheet/sheet';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { eventSessionsApi } from '@event-editor/api/eventSessions';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useSchedulePublishModal } from '@event-editor/hooks/eventEditorState';
import { isStartTimeBeforeScheduledPublish } from '@event-editor/utils';
import {
  useEventUpdateNotificationsModal,
  EventUpdateNotificationSettingsModal
} from '@event-editor/components/EventUpdateNotificationSettingsModal';
import { convertSendAtToDBValue } from 'pages/dashboard/overview/events/schemas/commonUtils';
import { useSessionTimeChangeValidations } from '@event-editor/components/EditEventDetailsSheet/validateEventTimeChanges';
import {
  TimeChangeConfirmModal,
  TimeChangeConfirmModalType
} from '@event-editor/components/EditEventDetailsSheet/TimeChangeConfirmModal';

import { EventSessionForm } from './EventSessionForm';
import {
  getUpdateSessionSchema,
  UpdateSessionFormValues,
  sessionToUpdateFormValues,
  formValuesToUpdateSessionApiPayload,
  formValuesToPublishSessionApiPayload
} from './schemas';
import { resetSessionTimeFields } from './sessionTimeUtils';

interface UpdateSessionSheetProps extends React.ComponentProps<typeof Sheet> {
  session: EditableSession;
}

const UpdateSessionSheet: React.FC<UpdateSessionSheetProps> = ({
  onOpenChange,
  session,
  ...props
}) => {
  const event = useEditableEvent();
  const eventTimezoneCode = event.timezoneCode!;
  const schedulePublishModal = useSchedulePublishModal();
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [saveChangesConfirmModalOpen, setSaveChangesConfirmModalOpen] = useState(false);
  const [isTimeChangeConfirmModalOpen, setIsTimeChangeConfirmModalOpen] = useState(false);
  const [pendingActionOnConfirm, setPendingActionOnConfirm] = useState<
    'update' | 'publish' | 'duplicate'
  >('update');
  const { showToast } = useToaster();
  const updateNotificationModal = useEventUpdateNotificationsModal({
    submitHandler: (notificationSettings) => {
      updateSessionMutation.mutate({
        eventId: event.id!,
        payload: formValuesToUpdateSessionApiPayload(form.getValues(), session, eventTimezoneCode),
        updateNotificationSettings: notificationSettings
      });
      updateNotificationModal.close();
    }
  });
  const form = useForm<UpdateSessionFormValues>({
    resolver: zodResolver(getUpdateSessionSchema(session, eventTimezoneCode)),
    mode: 'onChange',
    values: sessionToUpdateFormValues(session, eventTimezoneCode)
  });

  const [updatedStartDateTime, updatedEndDateTime] = form.watch(['startDateTime', 'endDateTime']);

  const timeChangeValidations = useSessionTimeChangeValidations(
    session.id,
    updatedStartDateTime,
    updatedEndDateTime
  );
  const hasTimeChangeValidations = timeChangeValidations.length > 0;

  // Check if start time is before scheduled publish time (component-level validation)
  const hasStartTimeBeforeScheduledPublishError = useMemo(() => {
    if (!updatedStartDateTime || !eventTimezoneCode) {
      return false;
    }
    const startDateTimeUTC = convertSendAtToDBValue(updatedStartDateTime, eventTimezoneCode);
    return isStartTimeBeforeScheduledPublish(startDateTimeUTC, event.publishSchedule);
  }, [updatedStartDateTime, eventTimezoneCode, event.publishSchedule]);

  useEffect(() => {
    if (props.open && !isPast) {
      // Trigger form validations to show errors for
      // invalid sessions immediately on opening the sheet
      form.trigger();
    }
  }, [props.open, session.hasEnded, event.hasEnded]);

  const { mutateAsync: fetchSessionChanges, isPending: fetchingSessionChanges } = useMutation({
    mutationFn: eventSessionsApi.getChanges,
    onError: (error) => {
      console.error('Failed to fetch session changes:', error);
      showToast('Session couldn’t be saved. Please try again later.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const updateSessionMutation = useMutation({
    mutationFn: eventSessionsApi.update,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session updated');

        // Refresh event and sessions from API
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(event.id!)
        });

        // Close the sheet
        handleOpenChange(false);
      } else {
        showToast('Failed to update session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to update session', { variant: ToasterVariant.ERROR });
    }
  });

  const duplicateSessionMutation = useMutation({
    mutationFn: eventSessionsApi.duplicate,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session duplicated');

        // Refresh event and sessions from API
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(event.id!)
        });

        // Close the sheet
        handleOpenChange(false);
      } else {
        showToast('Failed to duplicate session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to duplicate session', { variant: ToasterVariant.ERROR });
    }
  });

  const publishSessionMutation = useMutation({
    mutationFn: eventSessionsApi.publish,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session published');

        // Refresh event and sessions from API
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(event.id!)
        });

        // Close the sheet
        handleOpenChange(false);
      } else {
        showToast('Failed to publish session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to publish session', { variant: ToasterVariant.ERROR });
    }
  });

  const deleteSessionMutation = useMutation({
    mutationFn: eventSessionsApi.delete,
    onSuccess: (results) => {
      if (results.ok) {
        showToast('Session deleted');

        // Refresh event and sessions from API
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventSessions(event.id!)
        });

        // Close the sheet
        handleOpenChange(false);
      } else {
        showToast('Failed to delete session', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to delete session', { variant: ToasterVariant.ERROR });
    }
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      if (!open) {
        form.reset();
      }
    },
    [onOpenChange, form]
  );

  // Proceed with session update
  const proceedWithUpdate = async (data: UpdateSessionFormValues): Promise<void> => {
    if (event.isPublished && session.isPublished && (event?.totalGuests ?? 0) > 0) {
      // Check if Update Notifications modal should be opened
      const sessionChanges = await fetchSessionChanges({
        eventId: event.id!,
        payload: formValuesToUpdateSessionApiPayload(data, session, eventTimezoneCode)
      });

      if (sessionChanges?.shouldRequestSendUpdateNotificationOptions) {
        updateNotificationModal.open({
          canResetRsvps: sessionChanges.canResetRsvps
        });
        return;
      }
    }

    // Update session
    updateSessionMutation.mutate({
      eventId: event.id!,
      payload: formValuesToUpdateSessionApiPayload(data, session, eventTimezoneCode)
    });
  };

  // Handle form submission for save/update
  const onSubmit = async (data: UpdateSessionFormValues): Promise<void> => {
    // Check if there are time change validations for published session
    if (hasTimeChangeValidations && session.isPublished) {
      setPendingActionOnConfirm('update');
      setIsTimeChangeConfirmModalOpen(true);
      return;
    }

    await proceedWithUpdate(data);
  };

  // Proceed with session publish (after time change confirmation if needed)
  const proceedWithPublish = (data: UpdateSessionFormValues): void => {
    publishSessionMutation.mutate({
      eventId: event.id!,
      sessionId: session.id!,
      ...formValuesToPublishSessionApiPayload(data, eventTimezoneCode)
    });
  };

  const proceedWithSaveAndDuplicate = async (): Promise<void> => {
    if (session.isPublished && (event.totalGuests ?? 0) > 0) {
      // Check if Update Notifications modal should be opened
      const sessionChanges = await fetchSessionChanges({
        eventId: event.id!,
        payload: formValuesToUpdateSessionApiPayload(form.getValues(), session, eventTimezoneCode)
      });

      if (sessionChanges?.shouldRequestSendUpdateNotificationOptions) {
        // Open modal and submit duplicate session with update notification settings
        updateNotificationModal.open({
          canResetRsvps: sessionChanges.canResetRsvps,
          onSubmit: (updateNotificationSettings) => {
            duplicateSessionMutation.mutate({
              sessionId: session.id!,
              changesPayload: formValuesToUpdateSessionApiPayload(
                form.getValues(),
                session,
                eventTimezoneCode
              ),
              updateNotificationSettings
            });
            updateNotificationModal.close();
          }
        });
        return;
      }
    }

    // Changes does not require triggering update notifications modal; proceed with duplication
    duplicateSessionMutation.mutate({
      sessionId: session.id!,
      changesPayload: formValuesToUpdateSessionApiPayload(
        form.getValues(),
        session,
        eventTimezoneCode
      )
    });
  };

  // Publish session
  const handleSessionPublish = (data: UpdateSessionFormValues): void => {
    // Check if there are time change validations - if so, show confirmation modal
    if (hasTimeChangeValidations) {
      setPendingActionOnConfirm('publish');
      setIsTimeChangeConfirmModalOpen(true);
      return;
    }

    proceedWithPublish(data);
  };

  // Save changes and duplicate session
  const handleSaveAndDuplicate = useCallback((): void => {
    if (session.isPublished && hasTimeChangeValidations) {
      // Check if there are time change validations; TimeChangeConfirmationModal handles showing update notifications modal
      setPendingActionOnConfirm('duplicate');
      setIsTimeChangeConfirmModalOpen(true);
      return;
    }

    if (session.isPublished && form.formState.isDirty) {
      setSaveChangesConfirmModalOpen(true);
      return;
    }

    proceedWithSaveAndDuplicate();
  }, [session.isPublished, hasTimeChangeValidations, form.formState.isDirty]);

  const handleResetTime = useCallback((): void => {
    resetSessionTimeFields(form, session, eventTimezoneCode);
    setIsTimeChangeConfirmModalOpen(false);
  }, [form, session, eventTimezoneCode]);

  const handleConfirmTimeChange = useCallback(async (): Promise<void> => {
    setIsTimeChangeConfirmModalOpen(false);
    const data = form.getValues();

    if (pendingActionOnConfirm === 'publish') {
      proceedWithPublish(data);
    } else if (pendingActionOnConfirm === 'duplicate') {
      proceedWithSaveAndDuplicate();
    } else {
      await proceedWithUpdate(data);
    }
  }, [form, pendingActionOnConfirm]);

  const isPast = (event.isPublished && session.hasEnded) || event.hasEnded;
  const isDeleting = deleteSessionMutation.isPending;
  const isDuplicating = duplicateSessionMutation.isPending;
  const isUpdating =
    updateSessionMutation.isPending || publishSessionMutation.isPending || fetchingSessionChanges;

  const sheetTitle = useMemo(() => {
    if (isPast) {
      return 'Past session';
    }
    return session.isPublished ? 'Update session' : 'Update draft session';
  }, [isPast, session.isPublished]);

  return (
    <Sheet {...props} onOpenChange={handleOpenChange}>
      <SheetContent
        className="tw-m-2 tw-flex tw-h-[calc(100vh-16px)] tw-w-[480px] tw-flex-col tw-gap-0 tw-rounded-3xl tw-p-0 sm:tw-max-w-[480px]"
        aria-describedby=""
      >
        <SheetHeader className="tw-p-6">
          <SheetTitle className="tw-mb-0">{sheetTitle}</SheetTitle>
        </SheetHeader>

        <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-4 tw-overflow-auto tw-pb-6">
          <EventSessionForm
            id="update-session-form"
            form={form as unknown as UseFormReturn<FieldValues>}
            disabled={isPast}
            session={session}
            hasStartTimeBeforeScheduledPublishError={hasStartTimeBeforeScheduledPublishError}
            onUpdateScheduleClick={() => schedulePublishModal.open({ isReschedule: true })}
          />
        </div>

        <hr className="tw-my-0 tw-w-full tw-border-gray-300" />
        <SheetFooter className="tw-flex tw-w-full tw-flex-shrink-0 tw-justify-between tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-3">
            {!isPast && // No update action(s) when event is past
              (event.isPublished && !session.isPublished ? (
                // Actions to update draft session in a published event
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="tw-rounded-xl"
                      disabled={
                        isUpdating ||
                        isDeleting ||
                        isDuplicating ||
                        hasStartTimeBeforeScheduledPublishError
                      }
                      loading={isUpdating}
                    >
                      Update <ChevronDownIcon size={16} className="tw-ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onClick={() =>
                        form.handleSubmit(onSubmit, (errors) => {
                          console.error(errors);
                        })()
                      }
                    >
                      Save as draft
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        form.handleSubmit(handleSessionPublish, (errors) => {
                          console.error(errors);
                        })()
                      }
                    >
                      Publish
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  size="sm"
                  className="tw-rounded-xl"
                  loading={isUpdating}
                  disabled={
                    isUpdating ||
                    isDeleting ||
                    isDuplicating ||
                    hasStartTimeBeforeScheduledPublishError
                  }
                  onClick={() =>
                    form.handleSubmit(onSubmit, (errors) => {
                      console.error(errors);
                    })()
                  }
                >
                  Save
                </Button>
              ))}

            <Button
              variant="secondary"
              size="sm"
              className="tw-rounded-xl"
              loading={isDuplicating}
              disabled={
                !form.formState.isValid ||
                isDuplicating ||
                isUpdating ||
                isDeleting ||
                hasStartTimeBeforeScheduledPublishError
              }
              onClick={handleSaveAndDuplicate}
            >
              <CopyIcon size={16} className="tw-mr-1" />
              Duplicate
            </Button>
          </div>

          {!isPast && (
            <Button
              variant="ghost"
              size="sm"
              className="tw-rounded-xl tw-text-negative-foreground"
              loading={isDeleting}
              disabled={isDeleting || isUpdating || isDuplicating}
              onClick={() => setDeleteConfirmModalOpen(true)}
            >
              <Trash2Icon size={16} className="tw-mr-1" />
              Delete
            </Button>
          )}
        </SheetFooter>

        <EventUpdateNotificationSettingsModal {...updateNotificationModal.modalProps} />
        <AlertDialog open={deleteConfirmModalOpen} onOpenChange={setDeleteConfirmModalOpen}>
          <AlertDialogContent className="tw-max-w-96 tw-gap-5">
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="tw-text-sm tw-font-normal tw-leading-5">
              This session will be permanently deleted.{' '}
              {session.isPublished &&
                'Guests registered for this session will be removed. They will not be removed from other sessions.'}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={isDeleting}
                onClick={() =>
                  deleteSessionMutation.mutate({
                    eventId: event.id!,
                    sessionId: session.id!
                  })
                }
              >
                Delete session
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Save changes upon duplication confirmation modal */}
        <AlertDialog
          open={saveChangesConfirmModalOpen}
          onOpenChange={setSaveChangesConfirmModalOpen}
        >
          <AlertDialogContent className="tw-max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle>Save changes?</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="tw-text-sm tw-font-normal tw-leading-5">
              To duplicate this session, edits will be saved and published.
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="primary"
                disabled={isDuplicating}
                onClick={proceedWithSaveAndDuplicate}
              >
                Save and duplicate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TimeChangeConfirmModal
          open={isTimeChangeConfirmModalOpen}
          type={TimeChangeConfirmModalType.CONFIRM}
          onOpenChange={setIsTimeChangeConfirmModalOpen}
          timeChangeValidations={timeChangeValidations}
          isSession
          onReschedule={handleResetTime}
          onConfirm={handleConfirmTimeChange}
        />
      </SheetContent>
    </Sheet>
  );
};

export { UpdateSessionSheet };
